import { Alert, Autocomplete, Box, Button, CircularProgress, Grid, Skeleton, Stack, TextField, Typography } from "@mui/material";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNotify } from "../../../context/NotificationContext";
import { getApiErrorMessage } from "../../../modules/common/shared/util/ApiErrorUtil";
import { INDIAN_STATES } from "../../../utils/indianStates";
import { SchoolHeader } from "../../fees/receipt/receiptModel";
import { SettingsCard } from "../components/SettingsCard";
import { UnsavedChangesBar } from "../components/UnsavedChangesBar";
import { SchoolHeaderPreview } from "./SchoolHeaderPreview";
import {
  EMPTY_PROFILE,
  SchoolProfile,
  fetchSchoolLogo,
  fetchSchoolProfile,
  removeSchoolLogo,
  saveSchoolProfile,
  uploadSchoolLogo,
} from "./schoolProfileService";
import { LOGO_TYPES, SchoolProfileErrors, validateLogoFile, validateSchoolProfile } from "./schoolProfileValidation";

type FieldKey = Exclude<keyof SchoolProfile, "hasLogo">;

const FIELDS: { key: FieldKey; label: string; required?: boolean; md: number; inputMode?: "numeric" | "email" | "tel"; maxLength: number }[] = [
  { key: "name", label: "School Name", required: true, md: 12, maxLength: 150 },
  { key: "addressLine1", label: "Address Line 1", required: true, md: 12, maxLength: 150 },
  { key: "addressLine2", label: "Address Line 2", md: 12, maxLength: 150 },
  { key: "city", label: "City", required: true, md: 6, maxLength: 80 },
  { key: "state", label: "State", required: true, md: 6, maxLength: 80 },
  { key: "pincode", label: "Pincode", required: true, md: 6, inputMode: "numeric", maxLength: 6 },
  { key: "phone", label: "Phone", md: 6, inputMode: "tel", maxLength: 10 },
  { key: "email", label: "Email", md: 12, inputMode: "email", maxLength: 120 },
];

const normalize = (p: SchoolProfile | null): SchoolProfile => ({ ...EMPTY_PROFILE, ...(p ?? {}), addressLine2: p?.addressLine2 ?? "", phone: p?.phone ?? "", email: p?.email ?? "" });

const sameProfile = (a: SchoolProfile, b: SchoolProfile) => FIELDS.every(({ key }) => (a[key] ?? "").trim() === (b[key] ?? "").trim());

// What the document header will show, from what's typed so far (placeholders for empty parts)
const toHeader = (p: SchoolProfile, logo: string | null): SchoolHeader => {
  const place = [p.addressLine1, p.addressLine2, p.city, p.state].map((part) => part?.trim()).filter(Boolean).join(", ");
  const address = [place, p.pincode?.trim()].filter(Boolean).join(" - ");
  const contact = [p.phone?.trim(), p.email?.trim()].filter(Boolean).join(" · ");
  return { name: p.name.trim() || "School Name", address: address || "School address", contact: contact || undefined, logo: logo ?? undefined };
};

// Field errors from the backend's { error, details: [{ field, message }] }
const serverFieldErrors = (err: unknown): SchoolProfileErrors | null => {
  if (!axios.isAxiosError(err)) return null;
  const details = (err.response?.data as { details?: { field: string; message: string }[] })?.details;
  if (!Array.isArray(details) || details.length === 0) return null;
  return Object.fromEntries(details.map((d) => [d.field, d.message])) as SchoolProfileErrors;
};

/** Settings → School Profile: logo, name, address and contact details shown on receipts and other documents. */
export const SchoolProfileSection = () => {
  const notify = useNotify();
  const [saved, setSaved] = useState<SchoolProfile | null>(null);
  const [draft, setDraft] = useState<SchoolProfile>(EMPTY_PROFILE);
  const [logo, setLogo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState<SchoolProfileErrors>({});
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const profile = await fetchSchoolProfile();
      setSaved(profile);
      setDraft(normalize(profile));
      setLogo(profile?.hasLogo ? await fetchSchoolLogo() : null);
    } catch (err) {
      setLoadError(getApiErrorMessage(err, "Couldn't load the school profile."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const errors = useMemo(() => ({ ...validateSchoolProfile(draft), ...serverErrors }), [draft, serverErrors]);
  const hasChanges = !sameProfile(normalize(saved), draft);

  const change = (key: FieldKey, value: string) => {
    const cleaned = key === "pincode" || key === "phone" ? value.replace(/\D/g, "") : value;
    setDraft((current) => ({ ...current, [key]: cleaned }));
    setServerErrors(({ [key]: _, ...rest }) => rest);
    setSaveError(null);
  };

  const discard = () => {
    setDraft(normalize(saved));
    setShowErrors(false);
    setServerErrors({});
    setSaveError(null);
  };

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(validateSchoolProfile(draft)).length > 0) {
      setSaveError("Fix the highlighted fields, then save.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const result = await saveSchoolProfile(draft);
      setSaved(result);
      setDraft(normalize(result));
      setShowErrors(false);
      notify("School profile saved");
    } catch (err) {
      const fieldErrors = serverFieldErrors(err);
      if (fieldErrors) {
        setServerErrors(fieldErrors);
        setSaveError("Fix the highlighted fields, then save.");
      } else {
        setSaveError(getApiErrorMessage(err, "Couldn't save the school profile."));
      }
    } finally {
      setSaving(false);
    }
  };

  const chooseLogo = async (file: File | undefined) => {
    if (fileInput.current) fileInput.current.value = ""; // the same file can be chosen again
    if (!file) return;
    const problem = validateLogoFile(file);
    if (problem) {
      setLogoError(problem);
      return;
    }
    setLogoBusy(true);
    setLogoError(null);
    try {
      const result = await uploadSchoolLogo(file);
      setSaved((current) => (current ? { ...current, hasLogo: result.hasLogo } : result));
      setLogo(await fetchSchoolLogo());
      notify("Logo updated");
    } catch (err) {
      setLogoError(getApiErrorMessage(err, "Couldn't upload the logo."));
    } finally {
      setLogoBusy(false);
    }
  };

  const deleteLogo = async () => {
    setLogoBusy(true);
    setLogoError(null);
    try {
      const result = await removeSchoolLogo();
      setSaved((current) => (current ? { ...current, hasLogo: result.hasLogo } : result));
      setLogo(null);
      notify("Logo removed");
    } catch (err) {
      setLogoError(getApiErrorMessage(err, "Couldn't remove the logo."));
    } finally {
      setLogoBusy(false);
    }
  };

  if (loadError) {
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: 2 }}
        action={
          <Button color="inherit" size="small" onClick={load} sx={{ fontWeight: 600 }}>
            Retry
          </Button>
        }
      >
        {loadError}
      </Alert>
    );
  }

  if (loading) {
    return (
      <Stack spacing={2.5}>
        <Skeleton variant="rounded" height={150} sx={{ borderRadius: 3 }} />
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      {!saved && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Receipts show placeholder school details until this profile is saved.
        </Alert>
      )}

      {/* Logo */}
      <SettingsCard icon={<ImageRoundedIcon />} title="Logo" description="Printed at the top of receipts. PNG or JPG, up to 1 MB; a square image works best.">
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", sm: "center" }}>
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: 2.5,
              border: logo ? "1px solid #e5e7eb" : "1px dashed #cbd5e1",
              bgcolor: logo ? "#fff" : "#f8fafc",
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
              flexShrink: 0,
              position: "relative",
            }}
          >
            {logo ? (
              <Box component="img" src={logo} alt="School logo" sx={{ width: "100%", height: "100%", objectFit: "contain", p: 1 }} />
            ) : (
              <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, letterSpacing: "0.04em" }}>NO LOGO</Typography>
            )}
            {logoBusy && (
              <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.75)", display: "grid", placeItems: "center" }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<AddPhotoAlternateRoundedIcon />}
                disabled={!saved || logoBusy}
                onClick={() => fileInput.current?.click()}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                {logo ? "Replace logo" : "Upload logo"}
              </Button>
              {logo && (
                <Button color="error" startIcon={<DeleteOutlineRoundedIcon />} disabled={logoBusy} onClick={deleteLogo} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                  Remove
                </Button>
              )}
            </Stack>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
              {!saved ? "Save the school details below first, then add the logo." : logo ? "Shown on every receipt." : "Until a logo is added, receipts show a LOGO placeholder."}
            </Typography>
            {logoError && (
              <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }} onClose={() => setLogoError(null)}>
                {logoError}
              </Alert>
            )}
            <input ref={fileInput} type="file" accept={LOGO_TYPES.join(",")} hidden onChange={(e) => chooseLogo(e.target.files?.[0])} />
          </Box>
        </Stack>
      </SettingsCard>

      {/* Details */}
      <SettingsCard icon={<ApartmentRoundedIcon />} title="School details" description="Name, address and contact details shown on receipts and other documents.">
        <Grid container spacing={2}>
          {FIELDS.map((field) => {
            const error = showErrors || serverErrors[field.key] ? errors[field.key] : undefined;
            if (field.key === "state") {
              // A list, not free text: India's states and union territories (checked by the backend too)
              return (
                <Grid key={field.key} size={{ xs: 12, md: field.md }}>
                  <Autocomplete
                    options={INDIAN_STATES}
                    value={draft.state || null}
                    onChange={(_e, value) => change("state", value ?? "")}
                    autoHighlight
                    renderInput={(params) => <TextField {...params} size="small" label={field.label} required error={Boolean(error)} helperText={error} />}
                  />
                </Grid>
              );
            }
            return (
              <Grid key={field.key} size={{ xs: 12, md: field.md }}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  required={field.required}
                  value={draft[field.key] ?? ""}
                  onChange={(e) => change(field.key, e.target.value)}
                  error={Boolean(error)}
                  helperText={error ?? (field.key === "pincode" ? "6 digits" : field.key === "phone" ? "10 digits, optional" : undefined)}
                  slotProps={{ htmlInput: { maxLength: field.maxLength, inputMode: field.inputMode } }}
                />
              </Grid>
            );
          })}
        </Grid>
      </SettingsCard>

      {/* Preview */}
      <SettingsCard icon={<VisibilityRoundedIcon />} title="How your school appears on documents" description="Updates as you type. See the full receipt in Fees & Receipts.">
        <SchoolHeaderPreview header={toHeader(draft, logo)} />
      </SettingsCard>

      <UnsavedChangesBar open={hasChanges} saving={saving} error={saveError} onDiscard={discard} onSave={save} />
    </Stack>
  );
};

export default SchoolProfileSection;
